import { Box, IconButton, Icons, iconSize, webPalette, Link } from '@munifocus/ui';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandMark } from './BrandMark';
import { GlobalSearch } from './GlobalSearch';
import { useParticipantMe } from '../content/participant-access';

/** Ties the mobile menu button to the panel it toggles. */
const MOBILE_NAV_ID = 'site-mobile-nav';

const NAV = [
  { key: 'home', to: '/' },
  { key: 'bulletin', to: '/bulletin' },
  { key: 'training', to: '/training' },
  { key: 'services', to: '/services' },
  { key: 'about', to: '/about' },
] as const;

/**
 * The header's account link: a participant already signed in to their training
 * portal goes straight there ("My training"); everyone else gets the portal
 * chooser ("Sign in").
 */
export function accountLink(signedIn: boolean): { to: string; label: string } {
  return signedIn
    ? { to: '/portal', label: 'web.nav.myTraining' }
    : { to: '/signin', label: 'web.nav.signIn' };
}

export function SiteHeader() {
  const { t, i18n } = useTranslation();
  // The participant session is an httpOnly cookie, so ask the API; a visitor
  // without one gets a 401 (not retried) and keeps "Sign in".
  const participant = useParticipantMe();
  const account = accountLink(Boolean(participant.data));
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [shrunk, setShrunk] = useState(false);
  const lang = (i18n.language?.startsWith('fr') ? 'fr' : 'en') as 'en' | 'fr';

  useEffect(() => {
    // Hysteresis: shrink once we're clearly scrolled, only restore near the top.
    // The gap between the two thresholds is a dead zone so sub-pixel / momentum
    // scrolling around a single boundary can't flip the state back and forth
    // (which caused the header to jiggle between sizes).
    const onScroll = () =>
      setShrunk((prev) => {
        const y = window.scrollY;
        if (y > 80) return true;
        if (y < 20) return false;
        return prev;
      });
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  const toggleLang = () => {
    // Persistence to the language cookie is handled by the i18n `languageChanged`
    // listener wired up in initI18n — just change the language here.
    const next = lang === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(next).catch(() => {
      /* noop */
    });
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backgroundColor: shrunk ? webPalette.overlayDeepTealSticky : webPalette.green,
        backdropFilter: shrunk ? 'saturate(140%) blur(8px)' : 'none',
        color: webPalette.onDark,
        borderBottom: shrunk
          ? `1px solid ${webPalette.borderOnDarkFaint}`
          : '1px solid transparent',
        boxShadow: shrunk ? `0 6px 20px -16px ${webPalette.overlayBlackShadow}` : 'none',
        transition: 'background-color 240ms ease, box-shadow 240ms ease, border-color 240ms ease',
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 6 },
          py: shrunk ? 0.25 : 1.25,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          // Without a gap the wordmark ran straight into the first nav link. On a
          // phone the full gaps pushed the menu button off the right edge.
          gap: { xs: 1, sm: 1.5, lg: 4 },
          transition: 'padding 240ms ease',
        }}
      >
        <RouterLink to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <BrandMark compact />
        </RouterLink>
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: { xs: 0.75, sm: 2, lg: 4 } }}>
          <Box
            component="nav"
            aria-label={t('common.a11y.mainNavigation')}
            sx={{
              // From lg, not md: the French labels do not fit the bar between
              // 900 and 1200px, and pushed Connexion off the screen.
              display: { xs: 'none', lg: 'flex' },
              gap: 3.75,
              alignItems: 'stretch',
            }}
          >
            {NAV.map((n) => (
              <Box
                key={n.key}
                component={RouterLink}
                to={n.to}
                aria-current={isActive(n.to) ? 'page' : undefined}
                sx={{
                  position: 'relative',
                  fontSize: 15,
                  fontWeight: 500,
                  color: isActive(n.to) ? webPalette.onDark : webPalette.onDarkSubtle,
                  display: 'flex',
                  alignItems: 'center',
                  // "À propos" broke over two lines in French.
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s',
                  '&:hover': { color: webPalette.onDark },
                  '&::after': isActive(n.to)
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 4,
                        height: '2px',
                        backgroundColor: webPalette.teal,
                      }
                    : undefined,
                }}
              >
                {t(`web.nav.${n.key}`)}
              </Box>
            ))}
          </Box>
          {/* Discoverability for Cmd-K: the trigger prints the shortcut on itself. */}
          <Box sx={{ display: 'flex', alignSelf: 'center' }}>
            <GlobalSearch />
          </Box>
          {/*
            The visible label is the *other* language's code, so on its own the
            button announces as an ambiguous two-letter string. Name the action,
            and tag the code with its own `lang` so a synthesiser does not read
            "FR" as an English word (WCAG 3.1.2 / 4.1.2).
          */}
          <Box
            component="button"
            type="button"
            onClick={toggleLang}
            aria-label={t(
              lang === 'en' ? 'common.a11y.switchToFrench' : 'common.a11y.switchToEnglish',
            )}
            lang={lang === 'en' ? 'fr' : 'en'}
            sx={{
              alignSelf: 'center',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: { xs: 30, md: 36 },
              border: `1px solid ${webPalette.borderOnDarkButton}`,
              borderRadius: '5px',
              padding: { xs: '0 7px', md: '0 13px' },
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: webPalette.onDark,
              cursor: 'pointer',
              background: 'transparent',
              fontFamily: 'inherit',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: webPalette.surfaceOnDarkSoft,
                borderColor: webPalette.borderOnDarkHover,
              },
            }}
          >
            {lang === 'en' ? 'FR' : 'EN'}
          </Box>
          {/* Goes to the in-app chooser, not to a portal: there are two and the
              header can't know which one the visitor wants. */}
          <Box
            component={RouterLink}
            to={account.to}
            sx={{
              display: { xs: 'none', lg: 'inline-flex' },
              alignItems: 'center',
              alignSelf: 'center',
              borderRadius: '5px',
              px: 2.75,
              py: '11px',
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: '0.02em',
              color: webPalette.green,
              backgroundColor: webPalette.teal,
              textDecoration: 'none',
              transition: 'all 0.2s',
              '&:hover': { backgroundColor: webPalette.tealHover },
            }}
          >
            {t(account.label)}
          </Box>
          <IconButton
            aria-label={t(open ? 'common.a11y.closeMenu' : 'common.a11y.openMenu')}
            aria-expanded={open}
            aria-controls={MOBILE_NAV_ID}
            onClick={() => setOpen((v) => !v)}
            sx={{
              display: { xs: 'inline-flex', lg: 'none' },
              color: webPalette.onDark,
              alignSelf: 'center',
              height: 36,
              width: 36,
              p: 0,
            }}
          >
            <Icons.Menu size={iconSize.lg} />
          </IconButton>
        </Box>
      </Box>
      {open ? (
        // Was a bare Box: on mobile the site had no navigation landmark at all.
        <Box
          component="nav"
          id={MOBILE_NAV_ID}
          aria-label={t('common.a11y.mainNavigation')}
          sx={{
            display: { xs: 'block', lg: 'none' },
            borderTop: `1px solid ${webPalette.borderOnDarkSubtle}`,
            px: 3,
            pb: 2,
            pt: 1,
          }}
        >
          {NAV.map((n) => (
            <Link
              key={n.key}
              component={RouterLink}
              underline="none"
              color="inherit"
              to={n.to}
              onClick={() => setOpen(false)}
              sx={{
                display: 'block',
                py: 1.5,
                fontSize: 16,
                fontWeight: 500,
                color: webPalette.onDarkMuted,
                borderBottom: `1px solid ${webPalette.borderOnDarkFaint}`,
              }}
            >
              {t(`web.nav.${n.key}`)}
            </Link>
          ))}
          <Link
            component={RouterLink}
            underline="none"
            color="inherit"
            to={account.to}
            onClick={() => setOpen(false)}
            sx={{
              display: 'block',
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
              color: webPalette.teal,
            }}
          >
            {t(account.label)}
          </Link>
        </Box>
      ) : null}
    </Box>
  );
}

export { NAV as SITE_NAV };
